import React, { useState } from 'react';
import { useIaJobs } from '../context/IaJobsProvider';
import { useOnboardingPack } from '../context/OnboardingPackProvider';
import IaPhasedLoading from './IaPhasedLoading';

function AnimatedEllipsis() {
  return (
    <span className="inline-flex w-[1.1em]" aria-hidden>
      <span className="animate-pulse" style={{ animationDelay: '0ms' }}>
        .
      </span>
      <span className="animate-pulse" style={{ animationDelay: '200ms' }}>
        .
      </span>
      <span className="animate-pulse" style={{ animationDelay: '400ms' }}>
        .
      </span>
    </span>
  );
}

function OnboardingReviewBar({ review, onOpen, onDismiss }) {
  const label = review.count === 1 ? '1 treino pronto' : `${review.count} treinos prontos`;
  return (
    <div className="sticky top-0 z-50 border-b border-iron-accent/30 bg-iron-surface/95 backdrop-blur-md">
      <div className="max-w-app mx-auto flex w-full items-center gap-2 px-5 py-2.5">
        <button type="button" className="flex min-w-0 flex-1 items-center gap-3 text-left" onClick={onOpen}>
          <span className="inline-flex h-2 w-2 shrink-0 rounded-full bg-iron-accent" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="truncate font-mono text-[10px] uppercase tracking-wider text-iron-accent">
              Treinos sugeridos
            </p>
            <p className="truncate text-xs text-iron-text">
              {label} · <span className="text-iron-accent">Rever</span>
            </p>
          </div>
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          className="shrink-0 px-1 font-mono text-[10px] text-iron-muted hover:text-iron-text"
          aria-label="Ocultar barra de revisão"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

function FeedbackReviewBar({ review, onOpen, onDismiss }) {
  return (
    <div className="sticky top-0 z-50 border-b border-iron-info/35 bg-iron-surface/95 backdrop-blur-md">
      <div className="max-w-app mx-auto flex w-full items-center gap-2 px-5 py-2.5">
        <button type="button" className="flex min-w-0 flex-1 items-center gap-3 text-left" onClick={onOpen}>
          <span className="inline-flex h-2 w-2 shrink-0 rounded-full bg-iron-info" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="truncate font-mono text-[10px] uppercase tracking-wider text-iron-info">
              Análise pronta
            </p>
            <p className="truncate text-xs text-iron-text">
              {review.contextLabel || 'Feedback IA'} · <span className="text-iron-accent">Rever</span>
            </p>
          </div>
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          className="shrink-0 px-1 font-mono text-[10px] text-iron-muted hover:text-iron-text"
          aria-label="Ocultar barra de análise"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

export default function IaJobNotificationBar() {
  const {
    jobs,
    jobLabel,
    onboardingReview,
    openOnboardingReview,
    feedbackReview,
    openFeedbackReview,
    clearFeedbackReview,
  } = useIaJobs();
  const { clearDraft: clearOnboardingPackDraft } = useOnboardingPack();
  const [expanded, setExpanded] = useState(false);

  const dismissOnboardingReview = () => {
    clearOnboardingPackDraft();
  };

  const hasActiveJobs = jobs.length > 0;
  const hasOnboardingReview = onboardingReview != null && onboardingReview.count > 0;
  const hasFeedbackReview = feedbackReview != null && feedbackReview.markdown?.trim();

  if (!hasActiveJobs && !hasOnboardingReview && !hasFeedbackReview) return null;

  if (!hasActiveJobs && hasFeedbackReview && !hasOnboardingReview) {
    return (
      <FeedbackReviewBar
        review={feedbackReview}
        onOpen={openFeedbackReview}
        onDismiss={clearFeedbackReview}
      />
    );
  }

  if (!hasActiveJobs && hasOnboardingReview && !hasFeedbackReview) {
    return (
      <OnboardingReviewBar
        review={onboardingReview}
        onOpen={openOnboardingReview}
        onDismiss={dismissOnboardingReview}
      />
    );
  }

  return (
    <>
      {hasActiveJobs ? (
        <div className="sticky top-0 z-50 border-b border-iron-accent/30 bg-iron-surface/95 backdrop-blur-md">
          <button
            type="button"
            className="max-w-app mx-auto flex w-full items-center gap-3 px-5 py-2.5 text-left"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
          >
            <span className="inline-flex h-2 w-2 shrink-0 animate-pulse rounded-full bg-iron-accent" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="truncate font-mono text-[10px] uppercase tracking-wider text-iron-accent">
                Motor IA
                <AnimatedEllipsis />
              </p>
              <p className="truncate text-xs text-iron-text">{jobLabel(jobs[0])}</p>
            </div>
            <IaPhasedLoading phase={jobs[0].phase} compact barsOnly className="shrink-0" />
            <span className="font-mono text-[10px] text-iron-muted">{expanded ? '▴' : '▾'}</span>
          </button>
          {expanded && jobs.length > 1 && (
            <ul className="max-w-app mx-auto space-y-2 border-t border-iron-border/40 px-5 py-2">
              {jobs.slice(1).map((job) => (
                <li key={job.id} className="flex items-center justify-between gap-2">
                  <span className="truncate text-xs text-iron-muted">{jobLabel(job)}</span>
                  <IaPhasedLoading phase={job.phase} compact barsOnly />
                </li>
              ))}
            </ul>
          )}
          {expanded && jobs[0] && (
            <div className="max-w-app mx-auto border-t border-iron-border/40 px-5 py-3">
              <IaPhasedLoading phase={jobs[0].phase} barsOnly />
            </div>
          )}
        </div>
      ) : null}
      {hasFeedbackReview ? (
        <FeedbackReviewBar
          review={feedbackReview}
          onOpen={openFeedbackReview}
          onDismiss={clearFeedbackReview}
        />
      ) : null}
      {hasOnboardingReview ? (
        <OnboardingReviewBar
          review={onboardingReview}
          onOpen={openOnboardingReview}
          onDismiss={dismissOnboardingReview}
        />
      ) : null}
    </>
  );
}
